import FeatureGates, { type DynamicConfig } from '@atlaskit/feature-gate-js-client';
import { addFeatureFlagAccessed } from '@atlaskit/react-ufo/feature-flags-accessed';

type AssurePrimitives<T> = T extends string
	? string
	: T extends number
		? number
		: T extends boolean
			? boolean
			: T;

const DEFAULT_ENABLED_PARAM = 'isEnabled';

/**
 * Returns the value of a given parameter in an experiment config.
 *
 * @param {string} experimentName - The name of the experiment
 * @param {string} param - The name of the parameter to fetch from the experiment config
 * @param {T} defaultValue - The value to serve if the experiment or parameter do not exist, or if the returned value does not match the expected type. Do not pass `true` as a default value.
 */
export function expVal<T>(
	experimentName: string,
	param: string,
	defaultValue: T extends true ? never : T,
): AssurePrimitives<T> {
	// eslint-disable-next-line confluence-feature-gating/use-recommended-utils
	const value = FeatureGates.getExperimentValue(experimentName, param, defaultValue);
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	addFeatureFlagAccessed(`${experimentName}:${param}`, value as never);

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	return value as AssurePrimitives<T>;
}

/**
 * Returns the value of a given parameter in an experiment config, without firing exposure. Only use this if you plan on firing exposure manually.
 *
 * @param {string} experimentName - The name of the experiment
 * @param {string} param - The name of the parameter to fetch from the experiment config
 * @param {T} defaultValue - The value to serve if the experiment or parameter do not exist, or if the returned value does not match the expected type. Do not pass `true` as a default value.
 */
export function UNSAFE_expValNoExposure<T>(
	experimentName: string,
	param: string,
	defaultValue: T extends true ? never : T,
): AssurePrimitives<T> {
	// eslint-disable-next-line confluence-feature-gating/use-recommended-utils
	const value = FeatureGates.getExperimentValue(experimentName, param, defaultValue, {
		fireExperimentExposure: false,
	});
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	addFeatureFlagAccessed(`${experimentName}:${param}`, value as never);

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	return value as AssurePrimitives<T>;
}

/**
 * Checks if experiment parameter value equals provided comparator value.
 *
 * __Note__: Uses `null` as experiment parameter default value.
 *
 * @param {string} experimentName - The name of the experiment
 * @param {string} param - The name of the parameter to fetch from the experiment config
 * @param {T} comparatorValue - Value to compare against experiment parameter value. Do not pass `false` as comparator value.
 */
// eslint-disable-next-line confluence-feature-gating/inline-usage
export function expValEquals<T>(
	experimentName: string,
	param: string,
	comparatorValue: T extends false ? never : T,
): boolean {
	// eslint-disable-next-line confluence-feature-gating/inline-expVal-params
	return expVal(experimentName, param, null) === comparatorValue;
}

/**
 * Checks if experiment parameter value equals provided comparator value without firing exposure. Only use this if you plan on firing exposure manually using `manuallyLogExpExposure`.
 *
 * __Note__: Uses `null` as experiment parameter default value.
 *
 * @param {string} experimentName - The name of the experiment
 * @param {string} param - The name of the parameter to fetch from the experiment config
 * @param {T} comparatorValue - Value to compare against experiment parameter value. Do not pass `false` as comparator value.
 */
// eslint-disable-next-line confluence-feature-gating/inline-usage
export function UNSAFE_expValEqualsNoExposure<T>(
	experimentName: string,
	param: string,
	comparatorValue: T extends false ? never : T,
): boolean {
	// eslint-disable-next-line confluence-feature-gating/inline-expVal-params
	return UNSAFE_expValNoExposure(experimentName, param, null) === comparatorValue;
}

/**
 * Manually logs exposure for an experiment. Only use this if you used `UNSAFE_expValNoExposure` or `UNSAFE_expValEqualsNoExposure`.
 *
 * @param {string} experimentName - The name of the experiment
 */
export function manuallyLogExpExposure(experimentName: string) {
	FeatureGates.manuallyLogExperimentExposure(experimentName);
}

/**
 * Simplified experiment value retrieval with default parameter name.
 * Uses 'isEnabled' as the default parameter key for common boolean feature flags.
 *
 * @param {string} experimentName - The name of the experiment
 */
// eslint-disable-next-line confluence-feature-gating/inline-usage
export function isExperimentEnabled(experimentName: string): boolean {
	// eslint-disable-next-line confluence-feature-gating/inline-expVal-params
	return expValEquals(experimentName, DEFAULT_ENABLED_PARAM, true);
}

/**
 * Simplified experiment value retrieval with default parameter name, without firing exposure.
 * Uses 'isEnabled' as the default parameter key for common boolean feature flags.
 * Only use this if you plan on firing exposure manually using `manuallyLogExpExposure`.
 *
 * @param {string} experimentName - The name of the experiment
 */
// eslint-disable-next-line confluence-feature-gating/inline-usage
export function UNSAFE_isExperimentEnabledNoExposure(experimentName: string): boolean {
	// eslint-disable-next-line confluence-feature-gating/inline-expVal-params
	return UNSAFE_expValEqualsNoExposure(experimentName, DEFAULT_ENABLED_PARAM, true);
}

class ExperimentConfig {
	readonly _experimentName: string;

	readonly _config: DynamicConfig;

	constructor(experimentName: string, config: DynamicConfig) {
		this._experimentName = experimentName;
		this._config = config;
	}

	public get<T>(
		key: string,
		defaultValue: T,
		typeGuard?: (value: unknown) => value is T,
	): AssurePrimitives<T> {
		// Check if the CRITERION override mechanism is available
		if (
			window &&
			window.__CRITERION__ &&
			typeof window.__CRITERION__.getExperimentValueOverride === 'function'
		) {
			const overrideValue = window.__CRITERION__.getExperimentValueOverride(
				this._experimentName,
				key,
			);
			if (overrideValue !== undefined && overrideValue !== null) {
				addFeatureFlagAccessed(`${this._experimentName}:${key}`, overrideValue as never);
				return overrideValue as AssurePrimitives<T>;
			}
		}

		const value = this._config.get(key, defaultValue, typeGuard);
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		addFeatureFlagAccessed(`${this._experimentName}:${key}`, value as never);

		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		return value as AssurePrimitives<T>;
	}
}

const cache = new Map();
/**
 * Returns the config for an experiment, and a function to manually fire exposure.
 *
 * @param {string} experimentName - The name of the experiment
 */
export function UNSAFE_noExposureExp(
	experimentName: string,
): [config: ExperimentConfig, fireExposure: () => void] {
	let instance = cache.get(experimentName);

	if (instance == null) {
		const config = FeatureGates.getExperiment(experimentName, {
			fireExperimentExposure: false,
		});

		const fireExposure = () => FeatureGates.manuallyLogExperimentExposure(experimentName);
		instance = [new ExperimentConfig(experimentName, config), fireExposure];
		cache.set(experimentName, instance);
	}

	return instance;
}
